import { useEffect, useMemo, useState } from "react";
import { Loader2, Pencil, Trash2 } from "lucide-react";
import t from "@/lang/lang";
import { LocationType } from "@/services/backend/buildings/types/location-type";
import { AreaDialogButton } from "@/services/backend/buildings/components/area-dialog-button";
import { LevelManagerDialogButton } from "@/services/backend/buildings/components/level-manager-dialog-button";
import {
  type AreaFilter,
  filterAreas,
  findLevel,
  groupAreasByLevel,
  levelsOfType,
  noLevelKey,
  sortAreas,
} from "@/services/backend/buildings/lib/building-areas";
import { useBuildingsAreaDeleteMutation } from "@/services/backend/buildings/api/building-api";
import { type Area } from "@/services/backend/buildings/types/area";
import {
  type Building,
  type BuildingOrderBy,
} from "@/services/backend/buildings/types/building";
import { parseRTKQueryError } from "@/shared/components/errors/parse-r-t-k-query-error";
import { SearchFilter } from "@/shared/components/filter/search-filter";
import { OrderBySelect } from "@/shared/components/tables/order-by-select";
import { PaginationLimitOffset } from "@/shared/components/tables/pagination-limit-offset";
import { TableCard } from "@/shared/components/tables/table-card";
import { TableEmptyRow } from "@/shared/components/tables/table-empty-row";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/shared/components/ui/table";
import { Button } from "@/shared/components/ui/button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/shared/components/ui/alert-dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/shared/components/ui/select";
import { OrdrBy } from "@/shared/lib/ordr/ordrBy";
import { formatDateTime } from "@/shared/lib/utilities/date";
import { useToast } from "@/shared/hooks/use-toast";
import { PersistenceStateUsingContext } from "@/shared/lib/persistence-state/persistence-state-info";
import { usePersistenceContext } from "@/shared/lib/persistence-state/context";
import { PersistenceState } from "@/shared/lib/persistence-state/persistence-state";

const allLevelsKey = "__ALL_LEVELS__";
const defaultLimit = 10;
const defaultOrderBy: OrdrBy<BuildingOrderBy>[] = [
  { field: "createdAt", dsc: true },
];

type AreaScope = "all" | "inside" | "outside";

const scopeTypes: Record<AreaScope, LocationType | undefined> = {
  all: undefined,
  inside: LocationType.LocationTypeInside,
  outside: LocationType.LocationTypeOutside,
};

/**
 * Renders the levels and areas of a building. Both already travel with the
 * building, so filtering, sorting and paging happen in the browser.
 */
export function BuildingAreasTables({ building }: { building: Building }) {
  const [scope, setScope] = useState<AreaScope>("all");
  const [search, setSearch] = useState("");
  const [levelKey, setLevelKey] = useState(allLevelsKey);
  const [orderBy, setOrderBy] =
    useState<OrdrBy<BuildingOrderBy>[]>(defaultOrderBy);
  const [limit, setLimit] = useState(defaultLimit);
  const [offset, setOffset] = useState(0);

  const scopeType = scopeTypes[scope];

  const levelOptions = useMemo(
    () => levelsOfType(building, scopeType),
    [building, scopeType],
  );

  const filter = useMemo<AreaFilter>(
    () => ({
      search,
      type: scopeType,
      levelKey: levelKey === allLevelsKey ? undefined : levelKey,
      orderBy,
    }),
    [levelKey, orderBy, scopeType, search],
  );

  const matchingAreas = useMemo(
    () => sortAreas(filterAreas(building, filter), orderBy),
    [building, filter, orderBy],
  );

  const rows = useMemo(
    () => groupAreasByLevel(matchingAreas.slice(offset, offset + limit)),
    [limit, matchingAreas, offset],
  );

  return (
    <div className="grid gap-3">
      <div className="flex items-center justify-between gap-3">
        <div className="text-sm font-medium">{t("Bereiche")}</div>
        <PersistenceStateUsingContext className="shrink-0" />
      </div>
      <div className="flex flex-col gap-2 lg:flex-row lg:flex-wrap lg:items-center lg:justify-between">
        <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center">
          <SearchFilter
            filter={{ active: search !== "", values: [search] }}
            onFilterChange={(next) => {
              setSearch(next.values?.[0] ?? "");
              setOffset(0);
            }}
            placeholder={t("Nach Bereichen oder Ebenen suchen ...")}
            className="h-8 w-full sm:w-80"
          />
          <Select
            value={scope}
            onValueChange={(value) => {
              setScope(value as AreaScope);
              setLevelKey(allLevelsKey);
              setOffset(0);
            }}
          >
            <SelectTrigger className="h-8 w-full sm:w-44">
              <SelectValue placeholder={t("Bereichstyp")} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t("Alle Bereiche")}</SelectItem>
              <SelectItem value="inside">{t("Innenbereiche")}</SelectItem>
              <SelectItem value="outside">{t("Außenbereiche")}</SelectItem>
            </SelectContent>
          </Select>
          <Select
            value={levelKey}
            onValueChange={(value) => {
              setLevelKey(value);
              setOffset(0);
            }}
          >
            <SelectTrigger className="h-8 w-full sm:w-48">
              <SelectValue placeholder={t("Ebene")} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={allLevelsKey}>{t("Alle Ebenen")}</SelectItem>
              <SelectItem value={noLevelKey}>{t("Ohne Ebene")}</SelectItem>
              {levelOptions.map((level) => (
                <SelectItem key={level.id} value={level.id}>
                  {level.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <LevelManagerDialogButton building={building} scopeType={scopeType} />
          <OrderBySelect<BuildingOrderBy>
            orderBy={orderBy}
            onOrderByChange={(next) => {
              setOrderBy(next ?? defaultOrderBy);
              setOffset(0);
            }}
            fields={[
              { label: t("Name"), value: "name" },
              { label: t("Erstellt am"), value: "createdAt" },
              { label: t("Aktualisiert am"), value: "updatedAt" },
            ]}
          />
        </div>
        <AreaDialogButton
          building={building}
          defaultType={scopeType ?? LocationType.LocationTypeInside}
          triggerLabel={t("Bereich anlegen")}
        />
      </div>
      <TableCard>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{t("Bereich")}</TableHead>
              <TableHead>{t("Ebene")}</TableHead>
              <TableHead>{t("Bereichstyp")}</TableHead>
              <TableHead>{t("Erstellt am")}</TableHead>
              <TableHead>{t("Aktualisiert am")}</TableHead>
              <TableHead className="w-[120px] text-right">
                {t("Aktionen")}
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {matchingAreas.length === 0 && (
              <TableEmptyRow
                colSpan={6}
                text={emptyAreasText(building, filter)}
              />
            )}
            {rows.map((row) =>
              row.kind === "group" ? (
                <TableRow key={row.key}>
                  <TableCell
                    colSpan={6}
                    className="bg-muted/30 text-sm font-medium"
                  >
                    {row.levelId
                      ? `${t("Ebene")}: ${findLevel(building, row.levelId)?.name ?? ""}`
                      : t("Ohne Ebene")}
                  </TableCell>
                </TableRow>
              ) : (
                <AreaRow
                  key={row.area.id}
                  area={row.area}
                  building={building}
                />
              ),
            )}
          </TableBody>
        </Table>
      </TableCard>
      <PaginationLimitOffset
        offset={offset}
        limit={limit}
        totalCount={matchingAreas.length}
        setLimitOffset={(nextLimit, nextOffset) => {
          setLimit(nextLimit);
          setOffset(nextOffset);
        }}
        className="ml-auto"
      />
    </div>
  );
}

function emptyAreasText(building: Building, filter: AreaFilter): string {
  const areaTypeText =
    filter.type === LocationType.LocationTypeInside
      ? t("Innenbereiche")
      : filter.type === LocationType.LocationTypeOutside
        ? t("Außenbereiche")
        : t("Bereiche");

  if (filter.levelKey === noLevelKey) {
    return t(`Es sind keine ${areaTypeText} ohne Ebene vorhanden.`);
  }
  if (filter.levelKey !== undefined) {
    const levelName =
      findLevel(building, filter.levelKey)?.name ?? t("Unbekannt");
    return t(
      `Es sind keine ${areaTypeText} für die Ebene ${levelName} vorhanden.`,
    );
  }
  return t("Es sind noch keine Bereiche vorhanden.");
}

function AreaRow({ area, building }: { area: Area; building: Building }) {
  const { toast } = useToast();
  const { setPersistenceState } = usePersistenceContext();
  const [deleteArea, deleteState] = useBuildingsAreaDeleteMutation();

  useEffect(() => {
    if (deleteState.error) {
      setPersistenceState(PersistenceState.ERROR);
      toast({
        ...parseRTKQueryError(deleteState.error),
        variant: "destructive",
      });
      deleteState.reset();
    }
  }, [deleteState, setPersistenceState, toast]);

  useEffect(() => {
    if (deleteState.isSuccess) {
      setPersistenceState(PersistenceState.SAVED);
      deleteState.reset();
    }
  }, [deleteState, setPersistenceState]);

  const levelName = findLevel(building, area.levelId)?.name;

  return (
    <TableRow>
      <TableCell>{area.name}</TableCell>
      <TableCell>
        {levelName || (
          <span className="text-muted-foreground">{t("Ohne Ebene")}</span>
        )}
      </TableCell>
      <TableCell>{areaTypeLabel(area.type)}</TableCell>
      <TableCell>{formatDateTime(area.createdAt)}</TableCell>
      <TableCell>{formatDateTime(area.updatedAt)}</TableCell>
      <TableCell>
        <div className="flex justify-end gap-2">
          <AreaDialogButton
            building={building}
            defaultType={area.type}
            area={area}
            triggerLabel={t("Bearbeiten")}
            triggerIcon={<Pencil className="h-4 w-4" />}
            triggerVariant="outline"
            iconOnly
          />
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button variant="outline" size="sm">
                <Trash2 className="h-4 w-4" />
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>{t("Bereich löschen")}</AlertDialogTitle>
                <AlertDialogDescription>
                  {t("Dieser Bereich wird dauerhaft gelöscht.")}
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>{t("Abbrechen")}</AlertDialogCancel>
                <AlertDialogAction
                  onClick={() => {
                    setPersistenceState(PersistenceState.SAVING);
                    deleteArea({ id: building.id, areaId: area.id });
                  }}
                >
                  {deleteState.isLoading ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    t("Löschen")
                  )}
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      </TableCell>
    </TableRow>
  );
}

function areaTypeLabel(value: LocationType) {
  return value === LocationType.LocationTypeOutside
    ? t("Außenbereich")
    : t("Innenbereich");
}
